import type {
  IFamily,
  IMember,
  IStudent,
  IAuthorizationInfo,
  IImmunizationRecord,
  IImmunizationInfo,
  IStudentSchool,
} from 'src/interfaces/family'
import { basePersonTemplate, addressTemplate } from 'src/templates/common'
import { relationshipOptions, occupationOptions } from 'src/config/options'
import dayjs from 'dayjs'
import { DATE_FORMATS } from 'src/config/constants'
import { cloneDeep } from 'lodash-es'
import type {
  IRegGroup,
  IRegistration,
  IRegistrationProgram,
  IRegistrationRate,
} from 'src/interfaces/registration'

const open = dayjs()
const close = open.add(3, 'year')

const openDate = open.format(DATE_FORMATS.humanreadable)
const closeDate = close.format(DATE_FORMATS.humanreadable)

export const familyTemplate: IFamily = {
  name: '',
  email: '',
  members: [],
  students: [],
}

export const authInfoTemplate: IAuthorizationInfo = {
  restrictedAccessFileUrl: null,
  memberType: 'Legal Guardian',
  pickupAuthorization: 'Full',
  pickupNotes: null,
  volunteerExpiryDate: '',
  isFlagged: false,
  isAuthorizedToVolunteer: false,
}

export const memberTemplate: IMember = {
  authorizationInfo: cloneDeep(authInfoTemplate),
  personalContactInfo: cloneDeep(addressTemplate),
  workContactInfo: cloneDeep(addressTemplate),
  isCompletedProfileSetup: false,
  financialInfo: undefined,
  hasPadSetup: false,
  relationship: relationshipOptions[0],
  isPrimaryMember: false,
  isPortalMember: false,
  isPortalRestricted: false,
  isAuthorizedSponsor: false,
  isCreditCardAutoPayment: false,
  hasSignedCCAgreement: false,
  hasSignedPADAgreement: false,
  occupation: occupationOptions[0],
  ...cloneDeep(basePersonTemplate),
}

export const immunizationRecordTemplate: IImmunizationRecord = {
  vaccineId: undefined,
  name: '',
  nextDueDate: undefined,
  lastDoseDate: undefined,
  exemptionReason: undefined,
  doses: [],
  recordFileUrls: [],
  exemptionFileUrls: [],
}

export const immunizationInfoTemplate: IImmunizationInfo = {
  isImmunizationsUpToDate: false,
  immunizationRecord: [],
  immunizationRecordFileUrl: [],
}

export const schoolTemplate: IStudentSchool = {
  id: '',
  name: '',
}

export const studentTemplate: IStudent = {
  healthCareNumber: null,
  studentNumber: '',
  livesWithMemberIds: [],
  prohibitedFoodItems: [],
  grade: null,
  tags: [],
  immunizationInfo: cloneDeep(immunizationInfoTemplate),
  medicalConditions: [],
  medications: [],
  specialNeeds: [],
  dateOfBirth: '',
  customFields: [],
  lastContacted: '',
  notes: '',
  school: cloneDeep(schoolTemplate),
  pods: [],
  registrationBased: true,
  lastStatusChange: null,
  lastBillingDate: null,
  ...cloneDeep(basePersonTemplate),
  gender: '',
  signedAgreementsAndPolicies: [],
  careType: 'Regular Extended Care',
  isDropIn: false,
  registrations: [],
}

const administrationInfoTemplate = {
  startDate: dayjs(new Date()).format(DATE_FORMATS.date),
  endDate: dayjs(new Date()).format(DATE_FORMATS.date),
  timesToBeAdministered: ['12:00'],
  instructions: '',
}

export const medicationTemplate = {
  name: '',
  purpose: '',
  type: 'Regular',
  expiryDate: dayjs().add(7, 'day').format(DATE_FORMATS.date),
  dosage: '',
  administrationInfo: cloneDeep(administrationInfoTemplate),
  isInOriginalContainer: false,
  isChildNameOnLabel: false,
  isPrescribedByPhysician: false,
  guardianSignature: '',
  submissionDate: '',
  isNew: true,
}

const instructionsTemplate = {
  whenToManage: null,
  howToManage: null,
  followUp: null,
}

export const medicalConditionsTemplate = {
  name: '',
  description: '',
  instructions: cloneDeep(instructionsTemplate),
}

export const registrationGroupTemplate: IRegGroup = {
  id: 'Automatic',
  ageGroups: [],
  name: 'Automatic',
  capacity: 0,
}

export const registrationProgramTemplate: IRegistrationProgram = {
  id: '',
  name: '',
  programRollover: {
    checkDate: [''],
    isRepeatAnnually: true,
    annualRecurringDates: [],
    nextProgram: {
      id: 'None',
      name: 'None',
    },
  },
  registrationAges: [],
  programSchedule: {
    openDate: openDate,
    closeDate: closeDate,
    registrationCloseDate: null,
    registrationOpenDate: null,
    weeksForEarlyRegistrations: null,
    weeksForEarlyRegistrationsPortal: null,
    registrationCloseDatePortal: null,
    registrationOpenDatePortal: null,
    recurringDays: {},
    customDays: {},
  },
}

export const registrationRateTemplate: IRegistrationRate = {
  id: '',
  name: '',
  credits: {
    style: '',
  },
  rollover: {
    id: '',
    name: '',
    ageGroups: [],
    changeDate: '',
    graduateRate: {
      id: 'None',
      name: 'None',
    },
    changeRate: {
      id: 'None',
      name: 'None',
    },
  },
}

export const registrationTemplate: IRegistration = {
  id: '',
  studentId: '',
  studentName: '',
  parentId: null,
  type: '',
  program: registrationProgramTemplate,
  group: registrationGroupTemplate,
  rate: registrationRateTemplate,
  rateSchedule: [],
  school: null,
  grade: null,
  startDate: dayjs().format(DATE_FORMATS.date),
  endDate: undefined,
  isPaidDeposit: false,
  isChargedDeposit: false,
  isWaitlist: false,
  registrationSlots: {
    openDate: '',
    closeDate: '',
    recurringDays: {},
    customDays: {},
  },
  status: '',
  chain: null,
  currentAgeGroup: { name: '' },
  studentAge: 0,
  pods: [],
  deposits: [],
}
